import { Component, Input, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { INGESTION_POINT_TYPE_ID, DataIngestionService } from "../data-ingestion/data.ingestion.service";

@TypeManagerDecorator("workflow-action-rules")
@Component({
    selector: "workflow-action-rules",
    template: `
        <div class="action-rules-container">
            @if (selectedAction) {
                <div class="rules-header">
                    <h3>Rules for {{ selectedAction.actionName }}</h3>
                </div>

                <div class="form-selector">
                    <label>Form Context:</label>
                    <div class="form-controls">
                        @if (getActionFormContext(selectedAction)) {
                            <div class="form-context-info">
                                <strong>{{ getActionFormContext(selectedAction)?.itemName }}</strong>
                                <p>Using the form context selected for this action.</p>
                            </div>
                        } @else {
                            <div class="no-form-context-warning">
                                <p>
                                    <strong>No Form Context Selected:</strong> Please select a form context for this
                                    action to configure rules.
                                </p>
                            </div>
                        }
                    </div>
                </div>

                @if (getActionFormContext(selectedAction)) {
                    <div class="rules-editor">
                        @if (selectedAction.workflowActionId) {
                            <s25-ng-rule-tree
                                [category]="'workflowAction'"
                                [subCategory]="getActionSubCategory(selectedAction)"
                                [conditionFilterMap]="getActionConditionFilterMap(selectedAction)"
                                class="rule-tree-editor"
                            ></s25-ng-rule-tree>
                        } @else {
                            <div class="unsaved-action-warning">
                                <p>
                                    <strong>Action Not Saved:</strong> Please save the workflow process first before
                                    configuring rules for this action.
                                </p>
                            </div>
                        }
                    </div>
                } @else {
                    <div class="no-form-selected">
                        <p>Select a form context for this action to configure rules.</p>
                    </div>
                }
            } @else {
                <div class="no-action">
                    <p>No action selected.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .action-rules-container {
            padding: 20px;
        }

        .rules-header h3 {
            margin: 0 0 16px 0;
            color: #333;
        }

        .form-selector {
            margin-bottom: 20px;
        }

        .form-selector label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
        }

        .form-controls {
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
        }

        .form-context-info {
            color: #28a745;
        }

        .form-context-info strong {
            display: block;
            margin-bottom: 4px;
        }

        .form-context-info p {
            margin: 0;
            font-size: 14px;
        }

        .no-form-context-warning {
            color: #dc3545;
        }

        .no-form-context-warning p {
            margin: 0;
            font-size: 14px;
        }

        .unsaved-action-warning {
            color: #856404;
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 16px;
            text-align: center;
        }

        .unsaved-action-warning p {
            margin: 0;
            font-size: 14px;
        }

        .rules-editor {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            padding: 16px;
        }

        .rule-tree-editor {
            min-height: 200px;
        }

        .no-form-selected {
            text-align: center;
            padding: 40px 20px;
            color: #666;
            background: #f8f9fa;
            border-radius: 6px;
        }

        .no-action {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, S25RuleTreeModule],
})
export class WorkflowActionRulesComponent implements OnInit {
    @Input() selectedAction!: WorkflowTypes.WorkflowAction;
    @Input() workflowProcessId!: number;

    private formContext = signal<DropDownItem | null>(null);

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        await this.loadFormContext();
    }

    async loadFormContext() {
        if (this.selectedAction.actionItemId && this.selectedAction.actionItemTypeId === INGESTION_POINT_TYPE_ID) {
            // Create basic dropdown item with what we know
            const dropdownItem: DropDownItem = {
                itemId: this.selectedAction.actionItemId.toString(),
                itemTypeId: this.selectedAction.actionItemTypeId,
                itemName: `Form ${this.selectedAction.actionItemId}`,
            };
            this.formContext.set(dropdownItem);

            try {
                const ingestionPoint = await DataIngestionService.getIngestionPoint(this.selectedAction.actionItemId);
                if (ingestionPoint && ingestionPoint.ingestionPointName) {
                    // Update just the name with the actual form name
                    dropdownItem.itemName = ingestionPoint.ingestionPointName;
                    this.formContext.set(dropdownItem);
                }
            } catch (error) {
                console.error("Error loading ingestion point:", error);
                // Keep the basic item with fallback name
            }
        } else {
            this.formContext.set({
                itemId: this.selectedAction.actionItemId || 0,
                itemTypeId: this.selectedAction.actionItemTypeId || 0,
                itemName: `Context ${this.selectedAction.actionItemId || 0}`,
            } as DropDownItem);
        }
    }

    getActionFormContext(action: WorkflowTypes.WorkflowAction): DropDownItem | null {
        return this.formContext();
    }

    getActionSubCategory(action: WorkflowTypes.WorkflowAction): string {
        const formContext = this.getActionFormContext(action);
        if (!action.workflowActionId || !formContext?.itemId) {
            return "";
        }
        // Format: actionId & parentItemTypeId & parentItemId
        return `${action.workflowActionId}&${INGESTION_POINT_TYPE_ID}&${formContext.itemId}`;
    }

    getActionConditionFilterMap(action: WorkflowTypes.WorkflowAction): Record<string, string> {
        const formContext = this.getActionFormContext(action);
        if (!formContext?.itemId) return {};
        return {
            ingestionFieldsAll: "" + formContext.itemId,
        };
    }
}
